<template>
  <Teleport v-if="isMounted" to="#app">
    <Transition name="popup">
      <section
        v-if="uiState.isContactFormOpen"
        id="popup_contact"
        class="pos-fixed pos-t-0 pos-l-0 z-1000 w-100 h-100 o-y-auto t-primary bg-canvas"
      >
        <div
          class="pos-absolute pos-t-0 pos-r-0 z-index-10 d-block pd-regular cursor-pointer br-1px br-t-0px br-r-0px br-subtle hover:bg-accent ease-ease-400"
          @click="closeContactForm"
        >
          <!-- Крестик — белый растровый SVG, цвет из него не достать: инвертируем
               по теме тем же переключателем, что и логотипы клиентов. -->
          <img :alt="$t('form.close')" src="/images/icons/cross.svg" class="h-regular theme-invert"/>
        </div>

        <div class="w-100 w-max-[50rem] h-min-[100vh] mn-auto pd-t-extra pd-b-extra pd-l-big pd-r-big mobile:pd-l-medium mobile:pd-r-medium mobile:pd-t-big mobile:pd-b-big">
      <div v-if="success" class="d-flex flex-column flex-align-center t-center pd-t-extra pd-b-extra">
        <div class="i-extra radius-pill br-2px br-accent t-accent d-flex flex-center mn-b-medium">
          <IconCheck class="i-semi" />
        </div>
        <h3 class="h2 mn-b-regular">Received.</h3>
        <p class="p-small t-secondary">We'll be in touch shortly.</p>
      </div>

      <form v-else @submit.prevent="handleSubmit" class="d-flex flex-column gap-extra mobile:gap-big">
        <!-- What are you building -->
        <fieldset>
          <legend class="p-micro caption t-secondary d-block mn-b-semi">What are you building?</legend>
          <div class="d-flex flex-wrap gap-regular">
            <button
              v-for="type in PROJECT_TYPES"
              :key="type"
              type="button"
              @click="toggleProjectType(type)"
              class="p-medium mobile:p-regular bg-transparent br-0px br-b-2px pd-b-thin mn-r-medium cursor-pointer ease-ease-300"
              :class="formData.projectType.includes(type)
                ? 't-primary br-primary'
                : 't-primary-transp-30 br-transparent hover:t-primary-transp-60'"
            >
              {{ type }}
            </button>
          </div>
        </fieldset>

        <!-- Budget -->
        <fieldset>
          <legend class="p-micro caption t-secondary d-block mn-b-semi">Project budget (USD)</legend>
          <div class="d-flex flex-wrap gap-small">
            <button
              v-for="range in BUDGET_RANGES"
              :key="range"
              type="button"
              @click="formData.budget = range"
              class="p-micro pd-t-small pd-b-small pd-l-medium pd-r-medium radius-pill br-1px cursor-pointer ease-ease-200"
              :class="formData.budget === range
                ? 't-inverse bg-inverse br-inverse'
                : 't-secondary bg-transparent br-subtle hover:br-strong hover:t-primary'"
            >
              {{ range }}
            </button>
          </div>
        </fieldset>

        <!-- Description -->
        <fieldset>
          <legend class="p-micro caption t-secondary d-block mn-b-semi">Tell us about the project</legend>
          <textarea
            v-model="formData.description"
            rows="3"
            class="input"
            placeholder="What's the vision? What problem are you solving?"
          />
        </fieldset>

        <!-- Contact Details -->
        <fieldset>
          <legend class="p-micro caption t-secondary d-block mn-b-semi">Contact details</legend>
          <div class="cols-2 mobile:cols-1 gap-big mn-b-big">
            <input
              v-model="formData.name"
              type="text"
              required
              class="input"
              placeholder="Your Name"
            />
            <input
              v-model="formData.email"
              type="email"
              required
              class="input"
              placeholder="Email Address"
            />
          </div>
          <!-- Название компании стоит отдельной строкой под сеткой: утилиты не
               задают grid-column, а строка во всю ширину нужна именно ему. -->
          <input
            v-model="formData.company"
            type="text"
            class="input"
            placeholder="Company / Project Name"
          />
        </fieldset>

        <!-- Submit -->
        <div class="pd-t-big">
          <button
            type="submit"
            :disabled="isSubmitting"
            class="button button--inverse radius-pill"
          >
            <span>{{ isSubmitting ? 'Sending...' : 'Let\'s Build' }}</span>
            <IconArrowRight v-if="!isSubmitting" class="i-medium" />
          </button>
        </div>
      </form>
      </div>
      </section>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { uiState, closeContactForm } from '@/store/index.js';
import IconCheck from '@/components/icons/IconCheck.vue';
import IconArrowRight from '@/components/icons/IconArrowRight.vue';

const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});

const PROJECT_TYPES = ['Digital Product', 'AI-Powered Solution', 'Platform / SaaS', 'Mobile App', 'Brand & Identity', 'E-Commerce', 'Business Automation', 'Blockchain / Web3'];
const BUDGET_RANGES = ['< $5k', '$5k – $15k', '$15k – $50k', '$50k – $100k', '$100k+'];

const formData = reactive({
  projectType: [],
  budget: '',
  description: '',
  name: '',
  email: '',
  company: ''
});

const isSubmitting = ref(false);
const success = ref(false);

const toggleProjectType = (type) => {
  const index = formData.projectType.indexOf(type);
  if (index > -1) {
    formData.projectType.splice(index, 1);
  } else {
    formData.projectType.push(type);
  }
};


const handleSubmit = async () => {
  isSubmitting.value = true;

  try {
    const applicationData = {
      type: 'project_request',
      contacts: {
        name: formData.name,
        email: formData.email,
      },
      text: formData.description,
      data: {
        projectType: formData.projectType,
        budget: formData.budget,
        company: formData.company,
      },
    };

    await fetch('/api/applications/public', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(applicationData),
    });

    success.value = true;

    setTimeout(() => {
      success.value = false;
      closeContactForm();
      // Очистка формы
      formData.projectType = [];
      formData.budget = '';
      formData.description = '';
      formData.name = '';
      formData.email = '';
      formData.company = '';
    }, 3000);
  } catch (error) {
    console.error('Form submission error:', error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style lang="scss">
html.popup_contact_visible {
  overflow: hidden;
  height: 100% !important;
}

/* Popup animation */
.popup-enter-active {
  animation: popup-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.popup-leave-active {
  animation: popup-out 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes popup-in {
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes popup-out {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(100%);
  }
}
</style>
